import { useCallback, useEffect, useRef, useState } from "react";

import { useAPINFE2, useFetchNfePost, useToast } from "hooks";
import { getURLWithParams } from "utils/context";

export const useGetQueimaAndRisco = () => {
  const apiNfe = useAPINFE2();
  const [data, setData] = useState(undefined);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(
    (params = {}) => {
      setLoading(true);
      apiNfe
        .post("/tax-note/produtos-queima-risco", {
          ...params,
        })
        .then(values => {
          setTotalPages(values?.data?.length);
          setData(values?.data?.data);
          setLoading(false);
        })
        .catch(() => {
          setData(undefined);
          setTotalPages(undefined);
          setLoading(false);
        });
    },
    [apiNfe],
  );

  return { isLoading: loading, data, totalPages, fetch };
};

export const useQueimaAndRiscoActions = () => {
  const apiNfe = useAPINFE2();
  const [data, setData] = useState(undefined);
  const [loading, setLoading] = useState(false);

  const { addToast } = useToast();

  const onSend = useCallback(
    (params = {}) => {
      setLoading(true);
      apiNfe
        .get(
          getURLWithParams("/tax-note/produtos-queima-risco", {
            taxType: params.taxType,
            issue_date_start: params.issue_date_start,
            issue_date_end: params.issue_date_end,
            type_export: params.type_export,
            user_name: params?.user_name,
            user_email: params?.user_email,
            tipo: params?.tipo,
          }),
        )
        .then(() => {
          addToast({
            type: "success",
            title: "Solicitação em andamento...",
            description: `Iremos lhe enviar um link para download do arquivo...`,
          });
        })
        .catch(() => {
          setData(undefined);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [apiNfe, addToast],
  );

  return { isLoading: loading, data, onSend };
};

export const useQueimaAndRisco = (params = {}) =>
  useFetchNfePost("/tax-note/produtos-queima-risco", params);

export const useQueimaAndRiscoNotas = (params = {}) =>
  useFetchNfePost("/tax-note/produtos-queima-risco/notas", params);

const QUEIMA_POR_PRODUTO_CONCURRENCY = 4;

const sumQueima = produtos =>
  produtos.reduce(
    (acc, item) => ({
      pisCofins: acc.pisCofins + Number(item?.queima_caixa_pis_cofins || 0),
      icms: acc.icms + Number(item?.queima_caixa_icms || 0),
    }),
    { pisCofins: 0, icms: 0 },
  );

// Queima de caixa por cprod, para uma lista de produtos (RelatorioProdutosNovo).
// O endpoint só filtra um cprod por vez, então faz uma chamada por produto,
// no máximo QUEIMA_POR_PRODUTO_CONCURRENCY em paralelo. Resultado por cprod:
// { pisCofins, icms } somando todas as lojas; null = sem venda no período;
// { error: true } = falha na chamada. `refreshKey` (opcional) força nova
// busca quando muda, mesmo com os mesmos códigos (ex.: troca de cliente).
export const useQueimaCaixaPorProduto = ({
  cprods = [],
  period,
  refreshKey,
}) => {
  const apiNfe = useAPINFE2();
  const [data, setData] = useState({});
  const [isLoading, setLoading] = useState(false);
  const requestId = useRef(0);
  const start = period?.issue_date_start;
  const end = period?.issue_date_end;

  // O backend monta o SQL com o cprod direto na string — um apóstrofo
  // quebra a consulta, então esses códigos ficam de fora.
  const cprodsKey = cprods.filter(cprod => !cprod.includes("'")).join("\n");

  useEffect(() => {
    const id = ++requestId.current;
    const queue = cprodsKey ? cprodsKey.split("\n") : [];

    if (!queue.length || !start || !end) {
      setData({});
      setLoading(false);
      return;
    }

    setLoading(true);
    setData({});
    const result = {};

    const worker = async () => {
      while (queue.length && id === requestId.current) {
        const cprod = queue.shift();
        try {
          const res = await apiNfe.post("/tax-note/produtos-queima-risco", {
            cprod,
            issue_date_start: start, // eslint-disable-line camelcase
            issue_date_end: end, // eslint-disable-line camelcase
            taxType: "all",
            page: 1,
            limit: 50,
          });
          // v1 devolve data[0].produtos; o fallback v2, data[0].totais.produtos.
          const first = res?.data?.data?.[0];
          const produtos = (
            first?.produtos ||
            first?.totais?.produtos ||
            []
          ).filter(item => String(item?.cprod) === cprod);

          result[cprod] = produtos.length ? sumQueima(produtos) : null;
        } catch {
          result[cprod] = { error: true };
        }
      }
    };

    Promise.all(
      Array.from(
        { length: Math.min(QUEIMA_POR_PRODUTO_CONCURRENCY, queue.length) },
        worker,
      ),
    ).then(() => {
      if (id !== requestId.current) return;
      setData(result);
      setLoading(false);
    });
  }, [apiNfe, cprodsKey, start, end, refreshKey]);

  return { data, isLoading };
};
